if (!window.Maximize.loadedScript.includes("bulk-order.js")) {
  window.Maximize.loadedScript.push("bulk-order.js");

  let _abortCtrl = null;
  let _debounceTimer = null;
  window.Maximize.bulkOrderSearchCache = window.Maximize.bulkOrderSearchCache || {};
  const _searchCache = window.Maximize.bulkOrderSearchCache;

  const BULK_ORDER_STORAGE_KEY = "bulk_order_table_items";
  const BULK_ORDER_RESTORE_CONCURRENCY = 8;

  // Runs `worker` over `items` with at most `concurrency` calls in flight at once.
  async function runWithConcurrency(items, concurrency, worker) {
    let nextIndex = 0;
    async function processQueue() {
      while (nextIndex < items.length) {
        const currentItem = items[nextIndex++];
        await worker(currentItem);
      }
    }
    const workerCount = Math.min(concurrency, items.length);
    await Promise.all(Array.from({ length: workerCount }, processQueue));
  }

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {
      Alpine.data("xBulkOrder", (moneyFormat) => ({
        dropdownOpen: false,
        searchQuery: "",
        isSearching: false,
        hasSearched: false,  // a search has come back since the field was last emptied — the
                             // panel stays hidden until then, instead of flashing up empty
        searchResults: [], // flat array of variant rows from API
        searchError: "", // set when fetchResults fails (network/HTTP) — replaces the no_results message
        rowQtys: {}, // variantId → pending qty for a NOT-yet-added row (added rows read qty from tableItems)
        qtyErrors: {}, // variantId → quantity-rule error message for the SEARCH DROPDOWN row (empty = valid)
        _qtyErrorTimers: {}, // variantId → auto-hide timer for dropdown-row errors
        tableQtyErrors: {}, // variantId → quantity-rule error message for the ADDED-ITEMS TABLE row (kept separate so a dropdown error never shows on the table row and vice versa)
        _tableQtyErrorTimers: {}, // variantId → auto-hide timer for table-row errors
        _liveMessage: '',    // transient screen-reader announcement (see _announce)
        _liveNonce: 0,
        _lastQuery: null,    // last query onSearchInput acted on — stray `input` events are ignored

        tableItems: [], // [{variantId, productId, hasMultipleVariants, productTitle, variantTitle, sku, image, priceFormatted, priceCents, available, qty}]
        collapsedGroups: {}, // productId → boolean (truthy = collapsed) — the added-items TABLE groups only
        dropdownCollapsed: {}, // productId → boolean (truthy = collapsed) — the SEARCH DROPDOWN groups; independent from the table
        isSubmitting: false,
        submitError: "",
        isImporting: false,
        showRemoveAllConfirm: false,
        showRemoveItemConfirm: false,
        pendingRemoveVariantId: null,
        pendingRemoveGroupId: null,

        _cartDrawerId: null,
        _strings: {},
        _priceHiddenLabel: "", // pre-rendered hidden-price message (login / restricted), from server

        rowHover: {}, // rowId → transform string (directional hover-rect, single scope)

        onRowEnter(id) {
          this.rowHover = { ...this.rowHover, [id]: "translateY(0)" };
        },
        onRowLeave(event, id) {
          const rowEl = event.currentTarget;
          const { top, height } = rowEl.getBoundingClientRect();
          const toBottom = event.clientY - top > height / 2;
          this.rowHover = { ...this.rowHover, [id]: toBottom ? "translateY(101%)" : "translateY(-101%)" };
        },
        rowHoverTransform(id) {
          return this.rowHover[id] || "translateY(-101%)";
        },

        // Group the flat variant rows by product, preserving API order.
        get groupedResults() {
          const groups = [];
          const byId = {};
          this.searchResults.forEach((row) => {
            let group = byId[row.productId];
            if (!group) {
              // productImage = the product's FIRST media (featured), not the first variant's
              // image — the parent row represents the product, not a variant.
              group = byId[row.productId] = { productId: row.productId, productTitle: row.productTitle, handle: row.handle, productImage: row.productImage, variants: [] };
              groups.push(group);
            }
            group.variants.push(row);
          });
          return groups;
        },
        // Search shows everything the API returned, sold-out included (those rows render
        // dimmed with a "--" qty and can't be added). Derives the hidden-price flag + the
        // "From" price per product group.
        get filteredGroups() {
          return this.groupedResults.reduce((acc, group) => {
            const variants = group.variants;
            if (!variants.length) return acc;
            const prices = variants.map((variant) => (variant.priceCents != null ? variant.priceCents : variant.price));
            acc.push({
              ...group,
              variants,
              // Only a product with NO options renders as one plain row (no parent header, no
              // toggle, no indent). Comes from Shopify's product.has_only_default_variant —
              // NOT from the variant count, which can't tell "no options at all" from "one
              // option with a single value": the latter is a real variant and still needs the
              // parent/child toggle so the buyer sees which variant they are adding.
              hasOnlyDefaultVariant: !!variants[0].hasOnlyDefaultVariant,
              // can-hide-price is resolved per PRODUCT server-side, so every variant carries the
              // same flag — the parent row hides its "From" price the same way its variants do.
              priceHidden: variants.some(variant => variant.priceHidden),
              minPriceFormatted: formatMoney(Math.min(...prices), moneyFormat),
            });
            return acc;
          }, []);
        },
        // tableItems minus anything parked as marketUnavailable (see _parkUnresolvedVariants) —
        // parked items stay in tableItems/localStorage so a future restore can pick them back
        // up, but they must not show up, count, or total in the UI while parked.
        get visibleTableItems() {
          return this.tableItems.filter((tableItem) => !tableItem.marketUnavailable);
        },
        get hasItems() {
          return this.visibleTableItems.length > 0;
        },
        get hasReadyItems() {
          return this.visibleTableItems.some((tableItem) => !tableItem.isLoading && !tableItem.hasError && tableItem.available && !tableItem.marketUnavailable && !tableItem.priceHidden);
        },
        get selectedCountText() {
          const tmpl = this._strings.items_selected || "";
          return tmpl.replace("[[COUNT]]", this.visibleTableItems.length);
        },
        get grandTotalAmountFormatted() {
          const visible = this.visibleTableItems.filter((tableItem) => !tableItem.priceHidden);
          if (!visible.length) return "--";
          const totalCents = visible.reduce((sum, tableItem) => sum + this.effectiveUnitPrice(tableItem, tableItem.qty) * tableItem.qty, 0);
          return formatMoney(totalCents, moneyFormat);
        },
        get grandTotalLabelText() {
          return (this._strings.footer_total_label || "").replace("[[AMOUNT]]", "").trim();
        },
        // Groups tableItems by product for parent/child rendering. A product only renders
        // flat (no parent header/toggle) when it has NO options at all (hasOnlyDefaultVariant
        // — same flag the search dropdown uses). A product WITH options always renders as a
        // group, even while only one of its variants is currently in the table — the toggle
        // still communicates "this is one variant of a configurable product".
        // Falls back to `handle` while an item is still restoring from localStorage (before
        // its productId comes back from the server), so concurrently-restoring products
        // don't get merged under a shared `undefined` key.
        get groupedItems() {
          const groups = [];
          const groupIndexByKey = {};
          this.visibleTableItems.forEach((tableItem) => {
            const groupKey = tableItem.productId ?? tableItem.handle;
            if (!(groupKey in groupIndexByKey)) {
              groupIndexByKey[groupKey] = groups.length;
              groups.push({ productId: groupKey, isFlat: !!tableItem.hasOnlyDefaultVariant, items: [] });
            }
            groups[groupIndexByKey[groupKey]].items.push(tableItem);
          });
          return groups;
        },
        // Drives the section's single live region. A transient action message ("Added X") wins
        // over the search status so adding a row is announced; the next search replaces it.
        get searchStatusText() {
          if (this._liveMessage) return this._liveMessage;
          if (!this.dropdownOpen) return '';
          if (this.isSearching) return this._strings.searching || "";
          if (!this.searchQuery.trim()) return "";
          if (this.searchError) return this.searchError;
          // Count the product groups actually rendered, not the raw variant rows.
          const count = this.filteredGroups.length;
          if (count > 0) {
            const tmpl = this._strings.results_found || "";
            return tmpl.replace("[[COUNT]]", count);
          }
          const tmpl = this._strings.no_results || '';
          return tmpl.replace('__QUERY__', this.searchQuery);
        },

        // Speak a one-off message ("Added <product>") through the live region above. The
        // alternating zero-width space guarantees the region's text really changes when the
        // same item is announced twice in a row (ZWSP is not spoken). Never cleared on a
        // timer — expiring it would make the result count get read out all over again.
        _announce(title) {
          const label = this._strings.added || '';
          this._liveNonce++;
          this._liveMessage = (label ? label + ' ' : '') + title + '​'.repeat(this._liveNonce % 2);
        },

        init() {
          const stringsEl = this.$el.querySelector("[data-bulk-order-strings]");
          this._strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};

          const labelEl = this.$el.querySelector("[data-bulk-order-price-hidden-label]");
          this._priceHiddenLabel = labelEl ? labelEl.innerHTML.trim() : "";

          const cartDrawerEl = document.getElementById("CartDrawer");
          this._cartDrawerId = cartDrawerEl ? cartDrawerEl.dataset.sectionId : null;

          this.restoreTableItems();

          document.addEventListener(BULK_ORDER_EVENT.importStarted, () => {
            this.isImporting = true;
          });
          document.addEventListener("bulk-order:import-resolved", (event) => {
            this.isImporting = false;
            this._replaceImportedItems(event.detail.items);
          });
        },

        // Items come from bulk-order-import.js already shaped like tableItems entries
        // (spread of a variantRow + qty) via the `bulk-order:import-resolved` CustomEvent.
        // A CSV import replaces the whole table rather than merging — bulk-order-import.js's
        // onImportClick() confirms with the merchant first when the table already has rows.
        _replaceImportedItems(items) {
          if (!items || !items.length) return;

          this.tableItems = items;
          this.persistTableItems();
          this._pruneQtyErrors(); // drop errors for rows the replaced table no longer has
        },

        // Table emptied out (any removal path) → tell xBulkOrderImport (owns
        // hasUsedCsvImport) to reset, so the compact CSV controls bar disappears and the
        // empty-state "getting started" panel comes back. Plain document event, since
        // xBulkOrderImport is a separate Alpine component (mirrors bulk-order:import-resolved).
        _maybeResetCsvImportFlag() {
          if (!this.hasItems) document.dispatchEvent(new CustomEvent("bulk-order:table-cleared", { bubbles: true }));
        },

        persistTableItems() {
          try {
            const data = this.tableItems.map((tableItem) => ({
              variantId: tableItem.variantId,
              handle: tableItem.handle,
              qty: tableItem.qty,
            }));
            localStorage.setItem(BULK_ORDER_STORAGE_KEY, JSON.stringify(data));
          } catch (storageError) {}
        },

        restoreTableItems() {
          let savedItems = [];
          try {
            const raw = localStorage.getItem(BULK_ORDER_STORAGE_KEY);
            savedItems = raw ? JSON.parse(raw) : [];
          } catch (storageError) {}
          if (!Array.isArray(savedItems) || !savedItems.length) return;

          this.tableItems = savedItems
            .filter((savedItem) => savedItem && savedItem.variantId && savedItem.handle)
            .map((savedItem) => ({
              variantId: String(savedItem.variantId),
              handle: savedItem.handle,
              qty: savedItem.qty > 0 ? savedItem.qty : 1,
              isLoading: true,
              hasError: false,
              errorMessage: "",
              // Only the id, handle and quantity are persisted; everything else arrives with
              // _restoreProduct. Seed the fields the markup binds to so this half-built shape
              // cannot print "undefined" while the skeleton is up — x-html/x-text assign the raw
              // value to innerHTML/textContent, and undefined stringifies.
              tooltipHtml: "",
            }));

          const variantIdsByHandle = {};
          this.tableItems.forEach((tableItem) => {
            (variantIdsByHandle[tableItem.handle] = variantIdsByHandle[tableItem.handle] || []).push(tableItem.variantId);
          });

          runWithConcurrency(Object.keys(variantIdsByHandle), BULK_ORDER_RESTORE_CONCURRENCY, (handle) => this._restoreProduct(handle, variantIdsByHandle[handle]));
        },

        async _restoreProduct(handle, variantIds, isRetry) {
          try {
            const url = `${window.Shopify.routes.root}products/${handle}?section_id=bulk-order-restore-results`;
            const res = await fetch(url);

            // A 404 (or a render with no variant data) means the product doesn't resolve in
            // the CURRENT market — not necessarily gone for good. Park it (hidden, still
            // persisted) instead of dropping it, so switching back to a compatible market
            // brings it back on the next restore.
            if (res.status === 404) {
              this._parkUnresolvedVariants(variantIds);
              return;
            }
            if (!res.ok) throw new Error("[BulkOrder] restore HTTP " + res.status);

            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, "text/html");
            const scriptEl = doc.getElementById("bulk-order-restore-data");
            if (!scriptEl) {
              this._parkUnresolvedVariants(variantIds);
              return;
            }

            const variantRows = JSON.parse(scriptEl.textContent).map((variantRow) => this._shapeVariantRow(variantRow));

            // A variant missing from the response no longer exists → drop it. A variant that
            // exists but is sold out is KEPT in the table (dimmed, +/- disabled — see markup;
            // skipped on submit — see handleSubmit) instead of being silently removed.
            const missingIds = [];
            variantIds.forEach((variantId) => {
              const variantRow = variantRows.find((row) => row.variantId === variantId);
              if (!variantRow) {
                missingIds.push(variantId);
                return;
              }

              this.tableItems = this.tableItems.map((tableItem) => (tableItem.variantId === variantId ? { ...variantRow, qty: tableItem.qty, isLoading: false, hasError: false, errorMessage: "" } : tableItem));
            });
            if (missingIds.length) this._dropRestoredVariants(missingIds);
          } catch (fetchError) {
            if (fetchError.name !== "AbortError" && !isRetry) return this._restoreProduct(handle, variantIds, true);

            this.tableItems = this.tableItems.map((tableItem) => (variantIds.includes(tableItem.variantId) ? { ...tableItem, isLoading: false, hasError: true, errorMessage: this._strings.restore_failed || "Failed to load" } : tableItem));
          }
        },

        _dropRestoredVariants(variantIds) {
          this.tableItems = this.tableItems.filter((tableItem) => !variantIds.includes(tableItem.variantId));
          this.persistTableItems();
        },

        // Doesn't resolve in the current market — hide it from the table (visibleTableItems)
        // without dropping it from tableItems/localStorage, so a future restore (once the
        // market is compatible again) picks it back up automatically.
        _parkUnresolvedVariants(variantIds) {
          this.tableItems = this.tableItems.map((tableItem) => (variantIds.includes(tableItem.variantId) ? { ...tableItem, isLoading: false, marketUnavailable: true } : tableItem));
        },

        // Dropdown (attached under the search input) — modelled on xModalSearch: open on
        // focus, close on click-away / Escape. The panel itself only shows when there is a
        // query (see x-show in the markup). NOT a modal — no body scroll lock / focus trap.
        openDropdown() {
          if (this.isImporting) return;
          this.dropdownOpen = true;
        },

        closeDropdown(returnFocus) {
          // Hiding the panel must never drop focus into the void, so pull it back to the field
          // whenever focus was inside. False for click-away (focus already moved to whatever
          // was clicked), so closing that way can't steal focus.
          const hadFocus = !!this.$refs.dropdownPanel?.contains(document.activeElement);
          this.dropdownOpen = false;
          this._liveMessage = '';
          this._pruneQtyErrors(); // drop dropdown-row errors; table-item errors are kept
          if (_abortCtrl) {
            try {
              _abortCtrl.abort();
            } catch (abortError) {}
            _abortCtrl = null;
          }
          clearTimeout(_debounceTimer);
          this._releaseFocusTrap((returnFocus || hadFocus) ? this.$refs.searchInput : null);
        },

        // ---- Keyboard: Tab cycles inside the open results, Escape leaves -----------------
        // Uses the theme's shared focus trap ($store.xFocusElement, as predictive search does)
        // instead of a hand-rolled roving tabindex: Tab / Shift+Tab walk the search field and
        // every result control in DOM order and wrap around, Enter/Space activate (native on
        // buttons), Escape closes and hands focus back to the field.

        // (Re-)arm the trap. It has to be re-armed whenever the results change, because
        // trapFocus() resolves the first/last focusable ONCE, at call time. Passing no
        // element to focus — and clearing the store's remembered element first — keeps
        // re-arming from yanking focus out of whatever row the user is on.
        _armFocusTrap() {
          const store = Alpine.store('xFocusElement');
          const region = this.$refs.searchRegion;
          if (!store || !region?.id) return;
          store.activeElement = null;
          store.trapFocus(region.id, '');
        },
        _releaseFocusTrap(elementToFocus) {
          const store = Alpine.store('xFocusElement');
          if (!store) return;
          // Without an explicit target removeTrapFocus() focuses its remembered element;
          // clear it so closing by click-away doesn't snatch focus back.
          if (!elementToFocus) store.activeElement = null;
          store.removeTrapFocus(elementToFocus || null);
        },

        // Enter / ArrowDown from the search field jump to the first result's action.
        focusFirstRow() {
          this.$nextTick(() => {
            const first = this.$refs.dropdownPanel?.querySelector("[data-bulk-nav]");
            if (!first) return;
            first.focus();
            first.closest('.bulk-order__dropdown-row')?.scrollIntoView({ block: 'nearest' });
          });
        },
        moveRow(event, step) {
          const panel = this.$refs.dropdownPanel;
          if (!panel) return;
          const nav = [...panel.querySelectorAll("[data-bulk-nav]")].filter((el) => el.offsetParent !== null);
          if (!nav.length) return;
          let next = nav.indexOf(event.currentTarget) + step;
          if (next < 0) next = nav.length - 1;
          if (next >= nav.length) next = 0;
          nav[next].focus();
          nav[next].scrollIntoView({ block: "nearest" });
        },
        // Enter from the search input: a single variant result adds immediately; otherwise
        // move focus into the list.
        onSearchEnter() {
          const variants = this.filteredGroups.flatMap((group) => group.variants);
          if (variants.length === 1) {
            this.addVariant(variants[0].variantId);
            return;
          }
          this.focusFirstRow();
        },

        onSearchInput() {
          if (this.isImporting) return;
          this.dropdownOpen = true;
          const query = this.searchQuery.trim();
          // A type="search" field fires `input` without anyone typing: browsers clear it on
          // Escape, autofill writes into it, IME commits fire it. Each of those used to restart
          // the search (new spinner, results wiped, refetch) while the user was only moving
          // around with the keyboard — so act only when the query really changed. Returning
          // before clearTimeout also lets a pending fetch for this same query finish.
          if (query === this._lastQuery) return;
          this._lastQuery = query;
          this._liveMessage = ''; // a new search supersedes any "Added X" announcement
          this.searchError = ''; // a new search supersedes any prior failure
          clearTimeout(_debounceTimer);
          // Cleared input: there is nothing to search, so settle the state right away
          // instead of leaving the spinner on from the previous keystroke.
          if (!query) {
            if (_abortCtrl) { try { _abortCtrl.abort(); } catch(abortError) {} _abortCtrl = null; }
            this.isSearching = false;
            this.hasSearched = false;
            this.searchResults = [];
            this._pruneQtyErrors();
            return;
          }
          this.isSearching = true;
          // Deliberately NOT clearing searchResults here: emptying them mid-typing is what made
          // the panel sit there blank until the response landed. The previous results stay put
          // (the field's spinner shows a search is running) and are swapped when the new ones
          // arrive; before the very first response hasSearched keeps the panel hidden entirely.
          this._pruneQtyErrors(); // new search: clear stale dropdown-row errors (keep table)
          _debounceTimer = setTimeout(() => this.fetchResults(query), 300);
        },

        async fetchResults(query) {
          if (_abortCtrl) {
            try {
              _abortCtrl.abort();
            } catch (abortError) {}
            _abortCtrl = null;
          }
          if (!query) {
            this.searchResults = [];
            return;
          }
          const cacheKey = query;
          if (_searchCache[cacheKey]) {
            this.searchResults = _searchCache[cacheKey];
            this.isSearching = false;
            this.hasSearched = true;
            this.$nextTick(() => this._armFocusTrap());
            return;
          }

          const ctrl = new AbortController();
          _abortCtrl = ctrl;
          this.isSearching = true;

          try {
            // Hybrid: Predictive Search (SKU/variant title matching) + section rendering
            // Section runs can-hide-price server-side; title-matched products get all
            // variants via all_products[handle].variants in Liquid (no enrichment fetch needed).
            const url = Shopify.routes.root
              + 'search/suggest'
              + '?q=' + encodeURIComponent(query)
              + '&resources[type]=product'
              + '&resources[limit]=10'
              + '&resources[options][fields]=title,variants.title,variants.sku'
              // Sold-out products stay in the results (rendered dimmed, not addable).
              + '&resources[options][unavailable_products]=show'
              + '&section_id=bulk-order-search-results';

            const res = await fetch(url, { signal: ctrl.signal });
            if (!res.ok) throw new Error("[BulkOrder] HTTP " + res.status);

            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const scriptEl = doc.getElementById('bulk-order-results-data');
            if (!scriptEl) { this.searchResults = []; return; }

            const variantRows = JSON.parse(scriptEl.textContent).map((variantRow) => this._shapeVariantRow(variantRow));

            _searchCache[cacheKey] = variantRows;
            const panel = this.$refs.dropdownPanel;
            const focusWasInList = !!panel && panel.contains(document.activeElement);
            this.searchResults = variantRows;
            this.hasSearched = true; // there is now something to show — the panel may open
            this.$nextTick(() => {
              this._armFocusTrap(); // new rows → re-resolve the trap's first/last focusable
              // x-for destroys any row whose variant is gone; if that stranded focus on
              // <body> (where Escape no longer works), put it back on the search field.
              if (focusWasInList && !panel.contains(document.activeElement)) {
                this.$refs.searchInput?.focus();
              }
            });
          } catch (fetchError) {
            if (fetchError.name === 'AbortError') return;
            console.error('[BulkOrder] fetchResults error:', fetchError);
            // Let the same query be searched again after a failure, which onSearchInput's
            // "query unchanged" guard would otherwise swallow.
            this._lastQuery = null;
            // A dropped connection throws a bare TypeError (fetch itself never reaches the
            // network) and navigator.onLine confirms it — same test bulk-order-import.js uses.
            // Any other failure (bad HTTP status, malformed response) gets the same message:
            // there's nothing more actionable to tell the shopper either way.
            this.searchResults = [];
            this.hasSearched = true; // open the panel so the message is actually seen
            this.searchError = this._strings.network_error || '';
          } finally {
            this.isSearching = false;
            _abortCtrl = null;
          }
        },

        // Normalizes a raw variant row from either search endpoint (fetchResults/_restoreProduct)
        // into the shape rows/tableItems render against: string variantId (server sends a
        // number) + client-formatted price fields (Liquid's price is cents, an integer).
        _shapeVariantRow(variantRow) {
          return {
            ...variantRow,
            variantId: String(variantRow.variantId),
            priceFormatted: formatMoney(variantRow.price, moneyFormat),
            compareAtPriceFormatted: variantRow.compareAtPrice && variantRow.compareAtPrice !== variantRow.price ? formatMoney(variantRow.compareAtPrice, moneyFormat) : "",
            priceCents: variantRow.price,
          };
        },

        // Whether a variant is currently in the added-items table (single source of truth).
        isAdded(variantId) {
          return this.tableItems.some((tableItem) => tableItem.variantId === variantId);
        },

        // ---- Row action names / states --------------------------------------------------
        // One source of truth for each row's accessible name AND its disabled state, so the
        // label can never disagree with what activating the row actually does.
        rowFullTitle(row) {
          return row.variantTitle ? row.productTitle + ' ' + row.variantTitle : row.productTitle;
        },
        rowActionLabel(row) {
          const title = this.rowFullTitle(row);
          if (this.isAdded(row.variantId)) return (this._strings.added || '') + ' ' + title;
          if (!row.available) return (this._strings.sold_out || '') + ' ' + title;
          return (this._strings.add_product || '') + ' ' + title;
        },
        rowActionDisabled(row) {
          return !row.available || this.isAdded(row.variantId);
        },
        // The variants a product row would actually add (skips sold-out and already-added).
        groupAddable(group) {
          return group.variants.filter(row => row.available && !this.isAdded(row.variantId));
        },
        groupActionDisabled(group) {
          return this.groupAddable(group).length === 0;
        },
        groupActionLabel(group) {
          if (!this.groupActionDisabled(group)) return (this._strings.add_product || '') + ' ' + group.productTitle;
          const label = group.variants.some(row => row.available) ? this._strings.added : this._strings.sold_out;
          return (label || '') + ' ' + group.productTitle;
        },

        // Variant row action — ADD ONLY. Activating a row that is already in the table does
        // nothing (a full no-op, so its qty and error state are left untouched); removal is
        // only offered by the added-items table. Unavailable variants can't be added.
        addVariant(variantId) {
          if (this.isAdded(variantId)) return;
          const row = this.searchResults.find(searchRow => searchRow.variantId === variantId);
          if (!row || !row.available) return;
          this._addRowToTable(row);
          this._announce(this.rowFullTitle(row));
        },

        // Parent-product row action: add every available child variant to the table (skips
        // sold-out and variants already added). Toggling expand/collapse is handled separately
        // by the toggle button, not by clicking the row.
        addProduct(productId) {
          const group = this.filteredGroups.find((g) => g.productId === productId);
          if (!group) return;
          const rows = this.groupAddable(group);
          if (!rows.length) return;
          rows.forEach(row => this._addRowToTable(row));
          this._announce(group.productTitle);
        },

        // Add a variant row to the table with a rule-clamped qty (from rowQtys, default min).
        // Re-clamps the qty if the variant is somehow already present. Same table-item shape as
        // before → submit/persist/restore are unaffected. (Extracted from the old handleAddSelected.)
        _addRowToTable(row) {
          const rule = this._ruleFor(row);
          const qty = quantityRuleUtils.normalizeQuantity(this.getRowQty(row.variantId), rule);
          const existing = this.tableItems.find((tableItem) => tableItem.variantId === row.variantId);
          if (existing) {
            this.tableItems = this.tableItems.map((tableItem) => (tableItem.variantId === row.variantId ? { ...tableItem, qty } : tableItem));
          } else {
            this.tableItems = [...this.tableItems, { ...row, qty, isLoading: false, hasError: false, errorMessage: "", submitErrorMessage: "" }];
          }
          this._setQtyError(row.variantId, ""); // (re)added with a clamped valid qty → clear dropdown error
          this._setQtyError(row.variantId, "", null, 'table'); // and any stale table-row error
          this.persistTableItems();
        },

        // Shopify quantity rule (min/max/increment) for a row/item object (flat fields).
        _ruleFor(obj) {
          return quantityRuleUtils.normalizeRule(obj && obj.min, obj && obj.max, obj && obj.increment);
        },

        // Effective per-unit price (cents) after applying volume-pricing breaks for qty.
        effectiveUnitPrice(obj, qty) {
          let price = obj.priceCents != null ? obj.priceCents : obj.price;
          const breaks = obj.priceBreaks;
          if (Array.isArray(breaks)) {
            for (const b of breaks) {
              if (qty >= b.minimumQuantity) price = b.price;
            }
          }
          return price;
        },

        // Qty shown in a dropdown row: the table item's qty once added, else the pending
        // rowQtys value (default = rule min).
        getRowQty(variantId) {
          const item = this.tableItems.find((tableItem) => tableItem.variantId === variantId);
          if (item) return item.qty;
          const row = this.searchResults.find((r) => r.variantId === variantId);
          return this.rowQtys[variantId] ?? this._ruleFor(row).min;
        },

        // Per-unit price shown in a dropdown row, changing with the chosen quantity + volume pricing.
        rowUnitPriceFormatted(row) {
          return formatMoney(this.effectiveUnitPrice(row, this.getRowQty(row.variantId)), moneyFormat);
        },

        // True when the stepper can't move further in `delta`'s direction for this row.
        // Shared by the markup (dimming + aria-disabled) and by changeRowQty, so the state
        // announced to assistive tech and the actual behaviour can't drift apart.
        atQtyBound(row, delta) {
          const rule = this._ruleFor(row);
          const qty = this.getRowQty(row.variantId);
          return delta < 0 ? qty <= rule.min : (rule.max !== null && qty >= rule.max);
        },

        // Compact quantity-rule summary ("Inc: N . Min: N . Max: N"); only the non-default parts.
        ruleSummary(row) {
          const rule = this._ruleFor(row);
          const parts = [];
          if (rule.increment > 1) parts.push((this._strings.rule_inc || "Inc") + ": " + rule.increment);
          if (rule.min > 1) parts.push((this._strings.rule_min || "Min") + ": " + rule.min);
          if (rule.max !== null) parts.push((this._strings.rule_max || "Max") + ": " + rule.max);
          return parts.join(" . ");
        },

        // Dropdown-row stepper. Once added, delegate to the existing table stepper so there is
        // ONE mutation path (no parallel state → no desync). Before adding, write pending rowQtys.
        changeRowQty(variantId, delta, inputEl) {
          const stepRow = this.searchResults.find(r => r.variantId === variantId);
          // The stepper buttons are aria-disabled (not disabled, so they stay focusable and can
          // still say why), which is advisory only — enforce the bound here.
          if (stepRow && this.atQtyBound(stepRow, delta)) return;
          const rule = this._ruleFor(stepRow);
          const base = inputEl ? inputEl.value : this.getRowQty(variantId);
          const next = quantityRuleUtils.step(base, rule, delta);
          if (inputEl) inputEl.value = next; // state may be unchanged → :value would not re-render
          this._setQtyError(variantId, ""); // dropdown channel
          // +/- always yields a rule-valid value, so it commits straight to the table when the
          // variant is already added; otherwise it just updates the pending dropdown-row qty.
          if (this.isAdded(variantId)) {
            this._commitRowQtyToTable(variantId, next);
          } else {
            this.rowQtys = { ...this.rowQtys, [variantId]: next };
          }
        },

        setRowQty(variantId, event) {
          const row = this.searchResults.find((r) => r.variantId === variantId);
          // Validate in the DROPDOWN channel: an invalid entry shows the error on the dropdown
          // row only and does NOT touch the table — only a valid (or auto-corrected) qty commits
          // down to the table row below.
          const qty = this._validateRuleQty(variantId, event.target, this._ruleFor(row), event, 'row');
          if (qty === null) return;
          if (this.isAdded(variantId)) {
            this._commitRowQtyToTable(variantId, qty);
          } else {
            this.rowQtys = { ...this.rowQtys, [variantId]: qty };
          }
        },

        // Push a valid dropdown-row qty onto its table item (for already-added variants),
        // clearing any stale submit error. Keeps dropdown and table qty in sync WITHOUT routing
        // the dropdown's validation error into the table's error channel.
        _commitRowQtyToTable(variantId, qty) {
          this.tableItems = this.tableItems.map((tableItem) => (tableItem.variantId === variantId ? { ...tableItem, qty, submitErrorMessage: "" } : tableItem));
          this._setQtyError(variantId, "", null, 'table'); // committed a valid qty → clear any stale table error
          this.persistTableItems();
        },

        // Blur/change with an out-of-range value (not a number / below min / above max)
        // auto-corrects to min/max, commits it and shows the error for 5s; Enter — and a
        // commit with an in-range value that only misses the increment grid — shows a
        // persistent error and does NOT commit. Returns the quantity to commit, or null.
        // Unlike the product page, each row here commits independently, so correcting on
        // leave is the right behaviour and there is no click for it to swallow.
        _validateRuleQty(variantId, inputEl, rule, event, target) {
          if (quantityRuleUtils.isValid(inputEl.value, rule)) {
            this._setQtyError(variantId, "", null, target);
            const val = parseInt(inputEl.value, 10);
            inputEl.value = val;
            return val;
          }
          const v = parseInt(inputEl.value, 10);
          const belowMin = !Number.isInteger(v) || v < rule.min;
          const aboveMax = rule.max !== null && v > rule.max;
          // The mobile card's input commits on `change`, not `blur` (see bulk-order.liquid).
          // Only blur used to count as a commit, so on mobile a negative / non-numeric /
          // over-max entry fell through to the "persistent error, commit nothing" branch —
          // leaving the box holding the bad value instead of snapping to min/max.
          const isCommitEvent = event && (event.type === "blur" || event.type === "change");
          if (isCommitEvent && (belowMin || aboveMax)) {
            const message = quantityRuleUtils.buildError(inputEl.value, rule); // from the typed value
            const corrected = belowMin ? rule.min : rule.max;
            inputEl.value = corrected;
            this._setQtyError(variantId, message, 3000, target);
            return corrected;
          }
          this._setQtyError(variantId, quantityRuleUtils.buildError(inputEl.value, rule), 3000, target);
          return null;
        },

        // Central qtyErrors writer. `target` picks the channel: 'row' = the search-dropdown
        // row tooltip (qtyErrors), 'table' = the added-items table row tooltip (tableQtyErrors).
        // They are separate so a dropdown validation error never lights up the table row for the
        // same variant (and vice versa). Clears any pending auto-hide first so a newer message
        // (or clear) is never wiped by a stale timer. EVERY error auto-closes after a delay (5s by
        // default) — no bulk-order error is left persistent; `autoHideMs` overrides the delay.
        _setQtyError(variantId, message, autoHideMs, target) {
          const isTable = target === 'table';
          const mapKey = isTable ? 'tableQtyErrors' : 'qtyErrors';
          const timers = isTable ? this._tableQtyErrorTimers : this._qtyErrorTimers;
          clearTimeout(timers[variantId]);
          this[mapKey] = { ...this[mapKey], [variantId]: message };
          if (message) {
            timers[variantId] = setTimeout(() => {
              this[mapKey] = { ...this[mapKey], [variantId]: "" };
            }, autoHideMs || 3000);
          }
        },

        // Keep each error map only for variants still live in its own context: dropdown errors
        // for rows still in the search results, table errors for items still in the table. Drops
        // the rest so a stale error can't re-open its popover when a variant is re-materialized.
        _pruneQtyErrors() {
          const liveRows = new Set([...this.searchResults.map((r) => r.variantId), ...this.tableItems.map((i) => i.variantId)]);
          this.qtyErrors = Object.fromEntries(Object.entries(this.qtyErrors).filter(([k]) => liveRows.has(k)));
          const liveTable = new Set(this.tableItems.map((i) => i.variantId));
          this.tableQtyErrors = Object.fromEntries(Object.entries(this.tableQtyErrors).filter(([k]) => liveTable.has(k)));
        },

        getItemTotal(item) {
          if (item.priceHidden) return "--";
          return formatMoney(this.effectiveUnitPrice(item, item.qty) * item.qty, moneyFormat);
        },

        // Per-unit price shown in the added-items table, changing with volume pricing.
        itemUnitPriceFormatted(item) {
          return formatMoney(this.effectiveUnitPrice(item, item.qty), moneyFormat);
        },

        getGroupTotal(group) {
          if (group.items.some((item) => item.priceHidden)) return "--";
          const totalCents = group.items.reduce((sum, item) => sum + this.effectiveUnitPrice(item, item.qty) * item.qty, 0);
          return formatMoney(totalCents, moneyFormat);
        },

        // "From {price}" when the group's items span more than one price, else the plain price.
        // Computed only over variants whose price isn't hidden (see the group header's price
        // cell in the template — it swaps to the login message when ALL variants are hidden).
        getGroupPriceLabel(group) {
          const prices = group.items.filter((item) => !item.priceHidden).map((item) => item.priceCents);
          if (!prices.length) return "";
          const minCents = Math.min(...prices);
          const maxCents = Math.max(...prices);
          const formatted = formatMoney(minCents, moneyFormat);
          if (minCents === maxCents) return formatted;
          return (this._strings.price_from || "").replace("[[PRICE]]", formatted) || formatted;
        },

        toggleGroup(productId) {
          this.collapsedGroups = { ...this.collapsedGroups, [productId]: !this.collapsedGroups[productId] };
        },

        // Search-dropdown group expand/collapse — independent from the table's collapsedGroups.
        toggleDropdownGroup(productId) {
          const willCollapse = !this.dropdownCollapsed[productId];
          // Collapsing hides this group's variant rows (x-collapse ends at display:none), so
          // move focus out first if it sits on one of them — otherwise it is stranded on a
          // hidden element. Collapsing from the toggle button keeps focus put: the button
          // lives in the parent row, not in the wrapper being hidden.
          const groupEl = willCollapse ? document.getElementById('bo-group-' + productId) : null;
          const focusInGroup = !!groupEl?.contains(document.activeElement);
          this.dropdownCollapsed = { ...this.dropdownCollapsed, [productId]: willCollapse };
          this.$nextTick(() => {
            if (focusInGroup) this.$refs.searchInput?.focus();
            this._armFocusTrap(); // visible focusables changed → re-resolve first/last
          });
        },

        removeProductGroup(groupKey) {
          this._removeFromTable((tableItem) => (tableItem.productId ?? tableItem.handle) !== groupKey);
        },

        getImgUrl(url) {
          if (!url) return "";
          const clean = url.replace(/_\d+x\d*(?:_crop_\w+)?(\.\w+)/, "$1");
          return clean.replace(/(\.\w+)(\?|$)/, "_240x240$1$2");
        },

        // Snap-aware stepper (quantityRuleUtils.step), typed value as base; clears the
        // row's rule + submit errors and persists (new bulk-order behavior).
        changeTableQty(variantId, delta, inputEl) {
          this.tableItems = this.tableItems.map((tableItem) => {
            if (tableItem.variantId !== variantId) return tableItem;
            const rule = this._ruleFor(tableItem);
            const base = inputEl ? inputEl.value : tableItem.qty;
            const next = quantityRuleUtils.step(base, rule, delta);
            if (inputEl) inputEl.value = next; // state may be unchanged → :value would not re-render
            return { ...tableItem, qty: next, submitErrorMessage: "" };
          });
          this._setQtyError(variantId, "", null, 'table');
          this.persistTableItems();
        },

        setTableQty(variantId, event) {
          const item = this.tableItems.find((tableItem) => tableItem.variantId === variantId);
          const qty = this._validateRuleQty(variantId, event.target, this._ruleFor(item), event, 'table');
          if (qty === null) return;
          this.tableItems = this.tableItems.map((tableItem) => (tableItem.variantId === variantId ? { ...tableItem, qty, submitErrorMessage: "" } : tableItem));
          this.persistTableItems();
        },

        // Shared tail for every removal path (single item, whole product group): keeps
        // tableItems, localStorage, qtyErrors and the CSV-controls flag from drifting apart —
        // removeProductGroup used to skip _pruneQtyErrors, leaving stale entries behind.
        _removeFromTable(keepPredicate) {
          this.tableItems = this.tableItems.filter(keepPredicate);
          this.persistTableItems();
          this._pruneQtyErrors();
          this._maybeResetCsvImportFlag();
        },

        removeItem(variantId) {
          this._removeFromTable((tableItem) => tableItem.variantId !== variantId);
        },

        // Shared open/close for this component's confirm popups (Remove item, Remove all) —
        // same scroll-lock + focus-trap dance, differing only by which flag/refs they touch.
        _openConfirmPopup(flagName, dialogRefName, closeBtnRefName) {
          this[flagName] = true;
          document.body.classList.add("overflow-y-hidden");
          this.$nextTick(() => {
            Alpine.store("xFocusElement").trapFocus(this.$refs[dialogRefName].id, this.$refs[closeBtnRefName].id);
          });
        },

        _closeConfirmPopup(flagName) {
          this[flagName] = false;
          document.body.classList.remove("overflow-y-hidden");
          Alpine.store("xFocusElement").removeTrapFocus();
        },

        openRemoveItemConfirm(variantId) {
          this.pendingRemoveVariantId = variantId;
          this._openConfirmPopup("showRemoveItemConfirm", "removeItemDialog", "removeItemCloseBtn");
        },

        // Same popup as the single-item remove — reuses its dialog/flag, only the pending
        // target differs (a whole product group vs a single variant), see confirmRemoveItem().
        openRemoveGroupConfirm(groupId) {
          this.pendingRemoveGroupId = groupId;
          this._openConfirmPopup("showRemoveItemConfirm", "removeItemDialog", "removeItemCloseBtn");
        },

        closeRemoveItemConfirm() {
          this._closeConfirmPopup("showRemoveItemConfirm");
          this.pendingRemoveVariantId = null;
          this.pendingRemoveGroupId = null;
        },

        confirmRemoveItem() {
          const variantId = this.pendingRemoveVariantId;
          const groupId = this.pendingRemoveGroupId;
          this.closeRemoveItemConfirm();
          if (groupId != null) {
            this.removeProductGroup(groupId);
          } else {
            this.removeItem(variantId);
          }
        },

        openRemoveAllConfirm() {
          this._openConfirmPopup("showRemoveAllConfirm", "removeAllDialog", "removeAllCloseBtn");
        },

        closeRemoveAllConfirm() {
          this._closeConfirmPopup("showRemoveAllConfirm");
        },

        confirmClearAll() {
          this.closeRemoveAllConfirm();
          this.tableItems = [];
          this.persistTableItems();
          this._pruneQtyErrors();
          document.dispatchEvent(new CustomEvent("bulk-order:table-cleared", { bubbles: true }));

          this.$nextTick(() => {
            requestAnimationFrame(() => {
              this.$root.scrollIntoView({ behavior: "smooth", block: "center" });
            });
          });
        },

        // variantId → summed quantity across cart line items (a variant can appear more than
        // once, e.g. one line with a preorder property and one without).
        _quantitiesByVariant(cartItems) {
          const quantities = {};
          cartItems.forEach((cartItem) => {
            const variantId = String(cartItem.variant_id);
            quantities[variantId] = (quantities[variantId] || 0) + cartItem.quantity;
          });
          return quantities;
        },

        async _fetchCartQuantities() {
          const res = await fetch(`${window.Shopify.routes.root}cart.js`);
          if (!res.ok) throw new Error("[BulkOrder] cart.js HTTP " + res.status);
          const cart = await res.json();
          return this._quantitiesByVariant(cart.items);
        },

        _applyCartSectionUpdates(sectionsToRender, data) {
          sectionsToRender.forEach((cartSection) => {
            const sectionEl = document.querySelector(cartSection.selector);
            if (sectionEl && data.sections?.[cartSection.id]) {
              sectionEl.innerHTML = getSectionInnerHTML(data.sections[cartSection.id], cartSection.selector);
            }
          });

          const cartDrawerEl = document.getElementById("CartDrawer");
          if (cartDrawerEl) cartDrawerEl.isOpen = true;

          window.xCartHelper?.updateCurrentCountItem();
          document.dispatchEvent(
            new CustomEvent(CART_EVENT.cartUpdate, {
              bubbles: true,
              detail: { isItemCountChanged: true },
            })
          );
        },

        // Fallback: only runs when the combined cart/add.js call reports an error.
        // Diffs cart quantities before/after to work out exactly which items succeeded,
        // partially succeeded (hit a quantity limit), or fully failed.
        async _reconcileAfterPartialFailure(readyItems, beforeQuantities, sectionsToRender) {
          let afterCart;
          try {
            const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ updates: {}, sections: sectionsToRender.map((cartSection) => cartSection.id) }),
            });
            afterCart = await res.json();
          } catch (reconcileError) {
            console.error("[BulkOrder]", reconcileError);
            this.submitError = this._strings.add_to_cart_failed || "Failed to add to cart. Please try again.";
            return;
          }

          const afterQuantities = this._quantitiesByVariant(afterCart.items);

          const succeededIds = [];
          this.tableItems = this.tableItems.map((tableItem) => {
            const readyItem = readyItems.find((ready) => ready.variantId === tableItem.variantId);
            if (!readyItem) return tableItem;

            const beforeQty = beforeQuantities[tableItem.variantId] || 0;
            const afterQty = afterQuantities[tableItem.variantId] || 0;
            const addedQty = afterQty - beforeQty;

            if (addedQty >= readyItem.qty) {
              succeededIds.push(tableItem.variantId);
              return tableItem;
            }
            if (addedQty > 0) {
              return {
                ...tableItem,
                qty: readyItem.qty - addedQty,
                submitErrorMessage: this._strings.max_quantity_in_cart,
              };
            }
            return { ...tableItem, submitErrorMessage: this._strings.cart_error };
          });

          if (succeededIds.length) {
            const succeededSet = new Set(succeededIds);
            this.tableItems = this.tableItems.filter((tableItem) => !succeededSet.has(tableItem.variantId));
            this._maybeResetCsvImportFlag();
          }
          this.persistTableItems();

          if (succeededIds.length) {
            this._applyCartSectionUpdates(sectionsToRender, afterCart);
          }
        },

        async handleSubmit() {
          // Sold-out and price-hidden (guest, login-to-view-price) items are silently skipped —
          // left in the table as-is, no error shown.
          const readyItems = this.tableItems.filter((tableItem) => !tableItem.isLoading && !tableItem.hasError && tableItem.available && !tableItem.marketUnavailable && !tableItem.priceHidden);
          if (!readyItems.length || this.isSubmitting) return;

          this.isSubmitting = true;
          this.submitError = "";
          this.tableItems = this.tableItems.map((tableItem) => ({ ...tableItem, submitErrorMessage: "" }));

          const preorderKey = this._strings.preorder_key;
          const preorderMsg = this._strings.preorder_message;
          const items = readyItems.map((tableItem) => ({
            id: parseInt(tableItem.variantId, 10),
            quantity: tableItem.qty,
            ...(tableItem.isPreorder && preorderKey && preorderMsg ? { properties: { [preorderKey]: preorderMsg } } : {}),
          }));

          const sectionsToRender = window.xCartHelper?.getSectionsToRender(this._cartDrawerId) || [];
          const sections = sectionsToRender.map((cartSection) => cartSection.id);

          let beforeQuantities = null;
          try {
            beforeQuantities = await this._fetchCartQuantities();
          } catch (snapshotError) {
            console.error("[BulkOrder]", snapshotError);
          }

          try {
            const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items, sections }),
            });
            const data = await res.json();

            if (data.status === 422) {
              if (beforeQuantities) {
                await this._reconcileAfterPartialFailure(readyItems, beforeQuantities, sectionsToRender);
              } else {
                this.submitError = data.description;
              }
              return;
            }

            this._applyCartSectionUpdates(sectionsToRender, data);

            const submittedIds = new Set(readyItems.map((tableItem) => tableItem.variantId));
            this.tableItems = this.tableItems.filter((tableItem) => !submittedIds.has(tableItem.variantId));
            this.persistTableItems();
            this._pruneQtyErrors();
            this._maybeResetCsvImportFlag();
          } catch (addToCartError) {
            console.error("[BulkOrder]", addToCartError);
            this.submitError = this._strings.add_to_cart_failed || "Failed to add to cart. Please try again.";
          } finally {
            this.isSubmitting = false;
            // Tells xBulkOrderImport (separate component) to clear its Phase 2 import result
            // message — stale once THIS submit has resolved (full success, partial failure,
            // or a hard failure like a network error), same timing as bulk-order:import-resolved.
            document.dispatchEvent(new CustomEvent(BULK_ORDER_EVENT.cartSubmitResolved, { bubbles: true }));
          }
        },
      }));
    });
  });
}
